// A suggested routine cleanup: the exact threads, why each is here, and one "Archive these N"
// button that arms, then confirms. Readers can leave items out; the request carries exactly the
// items still shown. When the group is focused (j/k on the board, or a step in focus mode) the
// keys work too: 1 arms, 1 again or Enter confirms, Esc disarms. A batch touches many threads at
// once, so it keeps its review step: a single press never approves it, even though one archive
// alone runs on one press with Undo.
// Adapted from EveryInc/tend src/feed/RoutineActionGroupView.tsx (MIT, Copyright (c) 2026
// Every, Inc.).
import { memo, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { toast } from "sonner";
import type { RoutineGroupView, ThreadSummary } from "@shared/api";
import type { RoutineGroupItem } from "@shared/core";
import { addressLabel } from "@web/lib/addresses";
import { isApiError } from "@web/lib/api";
import { cn } from "@web/lib/cn";
import { feedApi } from "@web/lib/feedApi";
import { invalidateBoards } from "@web/lib/feedQueries";
import { registerUndo, undoWindowMs } from "@web/lib/undoRegistry";
import { useVerdictCommand } from "@web/measure/verdictStore";
import { groupTarget } from "@web/measure/verdicts";
import { useCommand } from "@web/shell/commands";
import { Button } from "@web/ui/Button";
import { Kbd } from "@web/ui/Kbd";
import { UnsubscribeButton } from "@web/unsubscribe/UnsubscribeButton";
import { cancelWork } from "./cardOps";
import { CONFIRM_GUARD_MS } from "./actions";
import { routineArmHolds, routineArmKey } from "./routineArm";
import { workPhaseLabel } from "./wording";

export const ROUTINE_STATUS_WORDS: Record<RoutineGroupView["status"], string> = {
  proposed: "Suggested cleanup",
  approved: "Approved cleanup",
  executing: "Cleaning up",
  completed: "Cleaned up",
  partial: "Partly cleaned up",
  stale: "Out of date",
  cancelled: "Cancelled",
};

export const threadsWord = (n: number) => `${n} ${n === 1 ? "thread" : "threads"}`;

/** The toast for a refused or failed routine approval (shared with the combined cleanup card). */
export function routineApproveFailure(error: unknown): void {
  if (isApiError(error, "not_available")) {
    toast("Archiving isn't wired up yet", { description: "Nothing was changed." });
  } else if (isApiError(error, "stale") || isApiError(error, "conflict")) {
    toast("This group changed. Here's the latest.");
  } else {
    toast("Couldn't archive just now. Nothing was changed.");
  }
}

/** The words under an item row for how its archive went. */
function outcomeWords(item: RoutineGroupItem): string | null {
  if (!item.outcome) return null;
  if (item.outcome === "archived") return "Archived";
  if (item.outcome === "ejected") return "Changed since, so it went back to review";
  return `Not archived${item.outcomeDetail ? `: ${item.outcomeDetail}` : ""}`;
}

export interface RoutineItemRowProps {
  item: RoutineGroupItem;
  thread: ThreadSummary | undefined;
  /** Left out of this cleanup (shown struck through). */
  out: boolean;
  /** The group is still proposed, so the exclude control shows. */
  proposed: boolean;
  onToggle: () => void;
  /** The unsubscribe dialog opened (an armed cleanup disarms). */
  onUnsubscribeOpen: () => void;
}

/** One thread in a routine cleanup: subject, sender, why, outcome, unsubscribe, leave out. */
export function RoutineItemRow({
  item,
  thread,
  out,
  proposed,
  onToggle,
  onUnsubscribeOpen,
}: RoutineItemRowProps) {
  const outcome = outcomeWords(item);
  return (
    <li className="flex flex-wrap items-start gap-x-3 gap-y-1 py-2">
      <div className={cn("min-w-0 flex-1", out && "opacity-50")}>
        <p className={cn("truncate text-sm font-medium text-ink", out && "line-through")}>
          {thread?.subject?.trim() || "(no subject)"}
        </p>
        <p className="truncate text-xs text-muted">
          {thread ? addressLabel(thread.latestFrom, thread.accountEmail) : item.threadKey}
        </p>
        <p className="text-xs text-ink-2">{item.reason}</p>
        {outcome ? <p className="text-xs text-muted">{outcome}</p> : null}
      </div>
      <div className="flex flex-wrap items-center gap-1">
        {thread?.latestFrom?.email ? (
          <UnsubscribeButton
            accountId={thread.accountId}
            accountEmail={thread.accountEmail}
            address={thread.latestFrom.email}
            name={thread.latestFrom.name}
            threadKey={thread.key}
            onOpen={onUnsubscribeOpen}
          />
        ) : null}
        {proposed ? (
          <button
            type="button"
            title="Exclude from this cleanup only; future mail is unchanged"
            onClick={onToggle}
            className="shrink-0 rounded-md px-2 py-0.5 text-xs text-muted hover:bg-sunk hover:text-ink"
          >
            {out ? "Put back" : "Leave out"}
          </button>
        ) : null}
      </div>
    </li>
  );
}

export interface RoutineGroupBlockProps {
  group: RoutineGroupView;
  /** Bind the keys (1, Enter, Esc) to this group. */
  active?: boolean;
  /** Shown as the board's selection. */
  selected?: boolean;
  /** Show the threads without a click (the board's side pane, focus mode). */
  defaultOpen?: boolean;
  id?: string;
  onSelect?: () => void;
  /** The archive was approved (focus mode moves on). */
  onApproved?: () => void;
}

export function RoutineGroupBlock({
  group,
  active = false,
  selected = false,
  defaultOpen = false,
  id,
  onSelect,
  onApproved,
}: RoutineGroupBlockProps) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(defaultOpen);
  const [left, setLeft] = useState<ReadonlySet<string>>(new Set());
  const [armedAt, setArmedAt] = useState<number | null>(null);
  const [armedKey, setArmedKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const proposed = group.status === "proposed";
  const included = group.items.filter((i) => !left.has(i.threadKey));
  const count = included.length;

  // More mail folded into the group (same id) disarms it: a confirm covers only what was armed.
  const armKey = routineArmKey(group);
  useEffect(() => {
    if (armedAt && !routineArmHolds(armedKey, group)) {
      setArmedAt(null);
      toast("This cleanup changed. Look again before confirming.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armKey]);

  useEffect(() => {
    if (!armedAt) return;
    confirmRef.current?.focus({ preventScroll: true });
    confirmRef.current
      ?.closest("[role=group]")
      ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [armedAt]);

  // Leaving the group (j/k elsewhere) disarms it: what is confirmed must be on screen.
  const [wasActive, setWasActive] = useState(active);
  if (wasActive !== active) {
    setWasActive(active);
    if (!active) setArmedAt(null);
  }

  const arm = () => {
    if (!proposed || !count || busy) return;
    setOpen(true);
    setArmedKey(armKey);
    setArmedAt(Date.now());
  };

  const approve = async () => {
    if (!armedAt || Date.now() - armedAt < CONFIRM_GUARD_MS || !count || busy) return;
    if (!routineArmHolds(armedKey, group)) {
      setArmedAt(null);
      return;
    }
    setBusy(true);
    try {
      const result = await feedApi.approveRoutine(
        group.id,
        included.map((i) => i.threadKey),
        group.itemsRev,
      );
      const window = undoWindowMs(result.undoUntil, Date.now());
      if (window) {
        const workId = result.work.id;
        registerUndo({
          message: `Archiving ${threadsWord(count)}…`,
          durationMs: window,
          undo: () => cancelWork(queryClient, workId),
          doneMessage: "Cancelled. Nothing changed in Gmail.",
        });
      } else {
        toast(`Archiving ${threadsWord(count)}…`);
      }
      onApproved?.();
    } catch (error) {
      routineApproveFailure(error);
    } finally {
      setBusy(false);
      setArmedAt(null);
      invalidateBoards(queryClient, group.feedId);
    }
  };

  // `!`: a thread in this cleanup that shouldn't be here (or should have been a card); U0.
  useVerdictCommand(() => groupTarget(group), active);

  const keysOn = active && proposed;
  useCommand("card.action1", () => (armedAt ? void approve() : arm()), keysOn);
  useCommand("card.confirm", () => void approve(), keysOn && !!armedAt);
  useCommand("card.cancel", () => setArmedAt(null), keysOn && !!armedAt);

  const toggleItem = (threadKey: string) => {
    setArmedAt(null);
    setLeft((prev) => {
      const next = new Set(prev);
      if (next.has(threadKey)) next.delete(threadKey);
      else next.add(threadKey);
      return next;
    });
  };

  return (
    <article
      id={id}
      aria-label={`${ROUTINE_STATUS_WORDS[group.status]}: ${group.label}`}
      aria-current={selected || undefined}
      data-routine-group={group.id}
      onClick={onSelect}
      className={cn(
        "relative rounded-xl border bg-memo/60 px-4 py-3",
        selected ? "border-accent/60 ring-1 ring-accent/40" : "border-line",
      )}
      onKeyDown={(e) => {
        if (e.key === "Escape" && armedAt) {
          e.preventDefault();
          e.stopPropagation();
          setArmedAt(null);
        }
      }}
    >
      {selected ? (
        <span aria-hidden className="absolute inset-y-3 left-0 w-[3px] rounded-r bg-accent" />
      ) : null}
      <div className="flex flex-col gap-2">
        <div className="min-w-0">
          <p className="mm-kicker">{ROUTINE_STATUS_WORDS[group.status]}</p>
          <h3 className="text-lg leading-snug">{group.label}</h3>
          <p className="text-sm text-ink-2">{group.summary}</p>
          {group.work && !proposed ? (
            <p className="mt-1 text-sm text-muted">{workPhaseLabel(group.work)}</p>
          ) : null}
        </div>
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1.5 self-start text-sm font-medium text-ink-2 hover:text-ink"
        >
          <ChevronRight
            aria-hidden
            className={cn("size-3.5 transition-transform", open && "rotate-90")}
          />
          {threadsWord(group.items.length)}
          <span className="font-normal text-muted">
            {proposed ? "· see each one and why" : "· details"}
          </span>
        </button>
        {open ? (
          <ul className="flex flex-col divide-y divide-line border-y border-line">
            {group.items.map((item) => (
              <RoutineItemRow
                key={item.threadKey}
                item={item}
                thread={group.threads[item.threadKey]}
                out={left.has(item.threadKey)}
                proposed={proposed}
                onToggle={() => toggleItem(item.threadKey)}
                onUnsubscribeOpen={() => setArmedAt(null)}
              />
            ))}
          </ul>
        ) : null}
        {proposed ? (
          armedAt ? (
            <div
              role="group"
              aria-label="Confirm archive"
              className="flex scroll-mb-6 flex-col gap-2 rounded-lg border border-accent/40 bg-accent-soft/60 px-3 py-2.5"
            >
              <p className="text-sm text-ink">
                Archive these {threadsWord(count)} in Gmail? Nothing is deleted; they stay in All
                Mail.
              </p>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs text-muted">
                  <Kbd>Enter</Kbd>
                  {active ? (
                    <>
                      {" "}
                      (or <Kbd>1</Kbd> again)
                    </>
                  ) : null}{" "}
                  to confirm · <Kbd>Esc</Kbd> to cancel
                </span>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setArmedAt(null)}>
                    Cancel
                  </Button>
                  <Button
                    ref={confirmRef}
                    size="sm"
                    variant="primary"
                    busy={busy}
                    onKeyDown={(e) => {
                      if (e.repeat) e.preventDefault();
                    }}
                    onClick={() => void approve()}
                    data-testid="routine-confirm"
                  >
                    Archive {count}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <Button
              size="sm"
              className="self-start"
              disabled={!count}
              aria-keyshortcuts={active ? "1" : undefined}
              onClick={arm}
            >
              Archive these {count}
              {active ? <Kbd>1</Kbd> : null}
            </Button>
          )
        ) : null}
      </div>
    </article>
  );
}

/** The group as one row in the board's list (wide screens show the block beside the list). */
export const RoutineGroupRow = memo(function RoutineGroupRow({
  group,
  id,
  selected,
  onSelect,
}: {
  group: RoutineGroupView;
  id: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <div
      id={id}
      role="option"
      aria-selected={selected}
      onClick={onSelect}
      data-routine-group={group.id}
      className={cn(
        "relative flex cursor-pointer flex-col gap-0.5 border-b border-line px-4 py-3 select-none md:px-5",
        selected ? "bg-accent-soft" : "bg-memo/60 hover:bg-sunk/50",
      )}
    >
      {selected ? (
        <span aria-hidden className="absolute inset-y-0 left-0 w-[3px] bg-accent" />
      ) : null}
      <span className="mm-kicker !text-[10px]">{ROUTINE_STATUS_WORDS[group.status]}</span>
      <p className="line-clamp-2 font-serif text-[16px] leading-snug font-semibold text-ink">
        {group.label}
      </p>
      <p className="line-clamp-2 text-sm text-muted">{group.summary}</p>
      <p className="mt-1 text-xs text-muted">{threadsWord(group.items.length)}</p>
    </div>
  );
});
